import { fetchConversation } from '@app/api'
import { getMessages, initialConversationItem } from '@app/helpers'
import {
  ExistThreadEventCapturedPayload,
  UnknownThreadEventCapturedPayload,
} from '@app/hooks'

import { handleForbiddenConversationError } from '@app/utils'

import { mapSuggestedActionFromEvent } from './mapSuggestedActionFromEvent'
import { ConversationItem } from '../../../../models'
import { ThreadMessageReceivedEvent } from '../../types'
import {
  ChatDispatch,
  GetReceivedUnreadStatus,
  LoadDependencies,
} from '../types'

export type HandleSuggestedActionCreatedParams = {
  event: ThreadMessageReceivedEvent
  conversation: ConversationItem | null
  dispatch: ChatDispatch
  getDependencies: LoadDependencies
  getReceivedUnreadStatus: GetReceivedUnreadStatus
  onConversationForbidden: () => void
}

export const handleSuggestedActionCreated = async ({
  event,
  conversation,
  dispatch,
  getDependencies,
  getReceivedUnreadStatus,
  onConversationForbidden,
}: HandleSuggestedActionCreatedParams): Promise<void> => {
  const suggestedAction = mapSuggestedActionFromEvent(event)

  if (!suggestedAction) {
    console.error(
      'SuggestedActionCreated event is missing required metadata',
      event
    )
    return
  }

  if (conversation) {
    const suggestedActions = [
      suggestedAction,
      ...(conversation.suggestedActions?.filter(
        (action) => action.id !== suggestedAction.id
      ) ?? []),
    ]

    conversation.suggestedActions = suggestedActions
    conversation.updateDate = suggestedAction.updateDate

    const payload: ExistThreadEventCapturedPayload = {
      conversation: {
        ...conversation,
        suggestedActions,
        updateDate: suggestedAction.updateDate,
      },
    }

    dispatch({
      type: 'EXIST_THREAD_EVENT_CAPTURED',
      payload,
    })

    return
  }

  let fetchedConversation: Awaited<ReturnType<typeof fetchConversation>>
  try {
    fetchedConversation = await fetchConversation(event.threadId)
  } catch (error) {
    if (handleForbiddenConversationError(error, onConversationForbidden)) {
      return
    }

    throw error
  }

  const { listings, accounts } = await getDependencies([fetchedConversation])

  const messages = getMessages(fetchedConversation.lastMessage)

  const payload: UnknownThreadEventCapturedPayload = {
    listings,
    accounts,
    conversation: {
      ...initialConversationItem,
      ...fetchedConversation,
      messages,
      total: 0,
      unread: getReceivedUnreadStatus(
        fetchedConversation.id,
        fetchedConversation.participants
      ),
      suggestedActions: [
        suggestedAction,
        ...(fetchedConversation.suggestedActions?.filter(
          (action) => action.id !== suggestedAction.id
        ) ?? []),
      ],
    },
  }

  dispatch({
    type: 'UNKNOWN_THREAD_EVENT_CAPTURED',
    payload,
  })
}
